import React, { useState, useEffect, useRef, useMemo, memo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

const CustomCarousel = ({
  items = [],
  renderItem,
  className = "",
  slidesPerView = 6,
  spaceBetween = 12,
  loop = true,
  breakpoints = {},
  alterClass = "",
  itemClass = "",
  hasButtons = false,
  modules = [],
  navButtons = {},
  ...rest
}) => {
  const {
    prevBtnClass = "",
    btnClassName = "",
    nextBtnClass = "",
    prevBtnInner = "‹",
    nextBtnInner = "›",
  } = navButtons;
  const swiperRef = useRef(null);
  const totalItems = items.length;
  const [currentSlidesPerView, setCurrentSlidesPerView] =
    useState(slidesPerView);
  const [currentSpaceBetween, setCurrentSpaceBetween] = useState(spaceBetween);

  /**
   * 🔹 Compute responsive breakpoints dynamically based on available items
   */
  const computedBreakpoints = useMemo(() => {
    const updated = {};
    Object.entries(breakpoints).forEach(([bp, config]) => {
      const shouldLoop = totalItems > (config.slidesPerView || slidesPerView);
      updated[bp] = {
        ...config,
        loop: shouldLoop && loop,
      };
    });
    return updated;
  }, [breakpoints, totalItems, loop, slidesPerView]);

  /**
   * 🔹 Base Swiper props with loop logic
   */
  const swiperProps = useMemo(
    () => ({
      slidesPerView,
      spaceBetween,
      loop: totalItems > slidesPerView && loop,
      breakpoints: computedBreakpoints,
      ...rest,
    }),
    [slidesPerView, spaceBetween, computedBreakpoints, totalItems, loop, rest]
  );

  /**
   * 🔹 Track active slidesPerView from window resize and breakpoints
   */
  useEffect(() => {
    const updateSlidesPerView = () => {
      let activeSlidesPerView = slidesPerView;
      let activeSpaceBetween = spaceBetween;
      const width = window.innerWidth;

      const bpKeys = Object.keys(breakpoints)
        .map(Number)
        .sort((a, b) => a - b);

      for (let i = 0; i < bpKeys.length; i++) {
        const bp = bpKeys[i];
        const nextBp = bpKeys[i + 1];
        const bpConfig = breakpoints[bp];

        if ((width >= bp && width < nextBp) || (!nextBp && width >= bp)) {
          activeSlidesPerView = bpConfig?.slidesPerView || slidesPerView;
          activeSpaceBetween = bpConfig?.spaceBetween || spaceBetween;
          break;
        }
      }

      setCurrentSlidesPerView(activeSlidesPerView);
      setCurrentSpaceBetween(activeSpaceBetween);
    };

    updateSlidesPerView();
    window.addEventListener("resize", updateSlidesPerView);
    return () => window.removeEventListener("resize", updateSlidesPerView);
  }, [breakpoints, slidesPerView, spaceBetween]);

  /**
   * 🔹 If total items are less than current slidesPerView, render a static grid instead of Swiper
   */
  if (totalItems <= currentSlidesPerView) {
    return (
      <div
        className={`d-flex ${alterClass}`.trim()}
        style={{ gap: `${currentSpaceBetween}px` }}
      >
        {items.map((item, i) => (
          <div key={item?.id || `${i}-slide`} className={itemClass}>
            {renderItem ? renderItem(item, i) : item}
          </div>
        ))}
      </div>
    );
  }

  /**
   * 🔹 Otherwise, render Swiper carousel
   */
  return (
    <>
      {hasButtons && (
        <button
          className={`${btnClassName} ${prevBtnClass}`}
          onClick={() => swiperRef.current?.slidePrev()}
        >
          {prevBtnInner}
        </button>
      )}
      <Swiper
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        className={`custom-swiper ${className}`.trim()}
        {...swiperProps}
        modules={modules}
      >
        {items.map((item, i) => (
          <SwiperSlide key={item?.id || `${i}-slide`}>
            {renderItem ? renderItem(item, i) : item}
          </SwiperSlide>
        ))}
      </Swiper>

      {hasButtons && (
        <button
          className={`${btnClassName} ${nextBtnClass}`}
          onClick={() => swiperRef.current?.slideNext()}
        >
          {nextBtnInner}
        </button>
      )}
    </>
  );
};

export default memo(CustomCarousel);

// import React, { useState, useEffect, useRef, useMemo, memo } from "react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import "swiper/css";

// const CustomCarousel = ({
//   items = [],
//   renderItem,
//   className = "",
//   slidesPerView = 6,
//   spaceBetween = 12,
//   loop = true,
//   breakpoints = {},
//   alterClass,
//   itemClass,
//   ...rest
// }) => {
//   const swiperRef = useRef(null);
//   const totalItems = items.length;
//   const [currentSlidesPerView, setCurrentSlidesPerView] =
//     useState(slidesPerView);
//   const [currentSpaceBetween, setCurrentSpaceBetween] = useState(spaceBetween);

//   // 🔹 Compute breakpoints dynamically with loop logic
//   const computedBreakpoints = useMemo(() => {
//     const updated = {};
//     Object.entries(breakpoints).forEach(([bp, config]) => {
//       const shouldLoop = totalItems > (config.slidesPerView || slidesPerView);
//       updated[bp] = {
//         ...config,
//         loop: shouldLoop && loop,
//       };
//     });
//     return updated;
//   }, [breakpoints, totalItems, loop, slidesPerView]);

//   // 🔹 Base Swiper props
//   const swiperProps = useMemo(
//     () => ({
//       slidesPerView,
//       spaceBetween,
//       loop: totalItems > slidesPerView && loop,
//       breakpoints: computedBreakpoints,
//       ...rest,
//     }),
//     [slidesPerView, spaceBetween, computedBreakpoints, totalItems, loop, rest]
//   );

//   // 🔹 Track active slidesPerView from Swiper
//   useEffect(() => {
//     const swiper = swiperRef.current;
//     if (!swiper) return;

// const updateSlidesPerView = () => {
//   let activeSlidesPerView = slidesPerView;
//   let activeSpaceBetween = spaceBetween;
//   const width = window.innerWidth;

//   // Determine current breakpoint slidesPerView
//   const bpKeys = Object.keys(breakpoints)
//     .map(Number)
//     .sort((a, b) => a - b);

//   for (let i = 0; i < bpKeys.length; i++) {
//     const bp = bpKeys[i];
//     const nextBp = bpKeys[i + 1];

//     // If width is within current breakpoint range
//     if ((width >= bp && width < nextBp) || (!nextBp && width >= bp)) {
//       activeSlidesPerView = breakpoints[bp].slidesPerView || slidesPerView;
//       activeSpaceBetween = breakpoints[bp].spaceBetween || spaceBetween;
//       break; // stop after finding matching breakpoint
//     }
//   }

//   setCurrentSlidesPerView(activeSlidesPerView);
//   setCurrentSpaceBetween(activeSpaceBetween);
// };

//     updateSlidesPerView();
//     window.addEventListener("resize", updateSlidesPerView);
//     return () => window.removeEventListener("resize", updateSlidesPerView);
//   }, [breakpoints, slidesPerView,spaceBetween]);

//   console.log(currentSlidesPerView);

//   if (totalItems < currentSlidesPerView) {
//     return (
//       <div
//         className={`d-flex justify-content-center ${alterClass}`}
//         style={{ gap: currentSpaceBetween }}
//       >
//         {items?.map((item, i) => (
//           <div key={item?.id || `${i}-slide`} className={itemClass}>
//             {renderItem ? renderItem(item, i) : item}
//           </div>
//         ))}
//       </div>
//     );
//   }
//   return (
//     <Swiper
//       onSwiper={(swiper) => (swiperRef.current = swiper)}
//       className={`custom-swiper mx-auto  ${className}`.trim()}
//       {...swiperProps}
//     >
//       {items?.map((item, i) => (
//         <SwiperSlide key={item?.id || `${i}-slide`}>
//           {renderItem ? renderItem(item, i) : item}
//         </SwiperSlide>
//       ))}
//     </Swiper>
//   );
// };

// export default memo(CustomCarousel);
